import { useState } from 'react';

import Settings from './components/Settings/Settings';
import About from './components/About/About';
import Debug from './components/Debug/Debug';
import AppDrawer from './components/Common/AppDrawer';

import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';

import {
    BrandVariants,
    FluentProvider,
    Theme,
    createDarkTheme,
    createLightTheme,
    makeStyles,
} from '@fluentui/react-components';

import { Provider } from 'react-redux';
import { store } from './utils/store';

import { IPublicClientApplication } from '@azure/msal-browser';
import { CustomNavigationClient } from './utils/NavigationClient';
import MainContent from './main/MainContent';
import useTheme from './utils/useTheme';

const fluentXDSTheme: BrandVariants = {
    10: '#010402',
    20: '#0E1C11',
    30: '#122F1A',
    40: '#123D20',
    50: '#124C26',
    60: '#0F5A2C',
    70: '#096A32',
    80: '#0A793A',
    90: '#31864C',
    100: '#4B935E',
    110: '#62A070',
    120: '#78AD83',
    130: '#8EBA96',
    140: '#A3C7AA',
    150: '#B9D5BE',
    160: '#CFE2D2',
};

const lightTheme: Theme = {
    ...createLightTheme(fluentXDSTheme),
};

const darkTheme: Theme = {
    ...createDarkTheme(fluentXDSTheme),
};

darkTheme.colorBrandForeground1 = fluentXDSTheme[110]; // use brand[110] instead of brand[100]
darkTheme.colorBrandForeground2 = fluentXDSTheme[120]; // use brand[120] instead of brand[110]

const useStyles = makeStyles({
    content: {
        display: 'flex', // Set the display to flex
        flexDirection: 'column', // Set the direction of flex items
        minHeight: '100vh', // Set minimum height to 100% of the viewport height
        '::-webkit-scrollbar': {
            width: '0px',
            background: 'transparent',
        },
    },
});

type DrawerType = 'settings' | 'about' | 'debug' | null;

export default function App() {
    const { instance: pca } = useMsal();

    const [activeDrawer, setActiveDrawer] = useState<DrawerType>(null);
    const [isDarkTheme, setIsDarkTheme] = useTheme();

    const toggleSettingsDrawer = (open: boolean) => () => {
        setActiveDrawer(open ? 'settings' : null);
    };

    const toggleAboutDrawer = (open: boolean) => () => {
        setActiveDrawer(open ? 'about' : null);
    };

    const toggleDebugDrawer = (open: boolean) => () => {
        setActiveDrawer(open ? 'debug' : null);
    };

    const closeDrawer = () => setActiveDrawer(null);

    const navigate = useNavigate();
    const navigationClient = new CustomNavigationClient(navigate);
    pca.setNavigationClient(navigationClient);

    // TODO: This is hard-coded to "dark" for now; need to make this toggle-able.
    const bootstrapTheme = "dark";

    return (
        <Provider store={store}>
            <FluentProvider theme={darkTheme}>
                <div className={useStyles().content} data-bs-theme={bootstrapTheme}>
                    <MainContent
                        toggleSettingsDrawer={toggleSettingsDrawer}
                        toggleAboutDrawer={toggleAboutDrawer}
                        toggleDebugDrawer={toggleDebugDrawer}
                        isDarkTheme={isDarkTheme}
                    />
                    <AppDrawer open={activeDrawer === 'settings'} onClose={closeDrawer}>
                        <Settings />
                    </AppDrawer>
                    <AppDrawer open={activeDrawer === 'about'} onClose={closeDrawer}>
                        <About />
                    </AppDrawer>
                    <AppDrawer open={activeDrawer === 'debug'} onClose={closeDrawer}>
                        <Debug />
                    </AppDrawer>
                </div>
            </FluentProvider>
        </Provider>
    );
}
